const capabilities = (): void => {
  const selectors = {
    section: '[data-capabilities]',
    accordion: '[data-accordion]',
    heading: '[data-accordion-heading]',
    content: '[data-accordion-content]',
  };

  const classes = {
    open: 'item--open',
  };

  // Desktop uses hover-to-open with row-matched heights; touch/small screens use tap-to-toggle
  const isHoverSupported = window.matchMedia('(hover: hover)').matches;
  const isLargeScreen = (): boolean => window.matchMedia('(min-width: 992px)').matches;
  const isDesktopMode = (): boolean => isHoverSupported && isLargeScreen();

  const sections = document.querySelectorAll<HTMLElement>(selectors.section);

  sections.forEach((section) => {
    const accordions = section.querySelectorAll<HTMLElement>(selectors.accordion);

    // Build accordion-to-content map for quick lookups
    const contentMap = new Map<HTMLElement, HTMLElement>();
    accordions.forEach((accordion) => {
      const content = accordion.querySelector<HTMLElement>(selectors.content);
      if (content) {
        contentMap.set(accordion, content);
      }
    });

    // Group accordions by row
    const topRowAccordions = Array.from(accordions).filter(
      (acc) => acc.dataset.row === 'top'
    );
    const bottomRowAccordion = Array.from(accordions).find(
      (acc) => acc.dataset.row === 'bottom'
    );

    // Cached heights
    let topRowMaxHeight = 0;
    let bottomRowHeight = 0;

    // Measure a single accordion's content height
    const measureHeight = (accordion: HTMLElement): number => {
      const content = contentMap.get(accordion);
      if (!content) return 0;

      const originalHeight = content.style.height;
      const originalMaxHeight = content.style.maxHeight;
      const originalVisibility = content.style.visibility;

      content.style.height = 'auto';
      content.style.maxHeight = 'none';
      content.style.visibility = 'hidden';
      void content.offsetHeight;

      const height = content.scrollHeight;

      content.style.height = originalHeight;
      content.style.maxHeight = originalMaxHeight;
      content.style.visibility = originalVisibility;

      return height;
    };

    // Calculate and cache heights (desktop rows share a height so cards line up)
    const calculateHeights = (): void => {
      // Top row: find max height among all top row accordions
      topRowMaxHeight = topRowAccordions.reduce((max, accordion) => {
        return Math.max(max, measureHeight(accordion));
      }, 0);

      // Bottom row: just its own height
      if (bottomRowAccordion) {
        bottomRowHeight = measureHeight(bottomRowAccordion);
      }
    };

    // Get the appropriate height for an accordion based on mode and row
    const getHeightForAccordion = (accordion: HTMLElement): number => {
      if (!isDesktopMode()) {
        return measureHeight(accordion);
      }
      return accordion.dataset.row === 'top' ? topRowMaxHeight : bottomRowHeight;
    };

    const openAccordion = (accordion: HTMLElement): void => {
      const content = contentMap.get(accordion);
      if (!content) return;

      accordion.classList.add(classes.open);
      content.style.height = getHeightForAccordion(accordion) + 'px';
    };

    const closeAccordion = (accordion: HTMLElement): void => {
      const content = contentMap.get(accordion);
      if (!content) return;

      accordion.classList.remove(classes.open);
      content.style.height = '0px';
    };

    // Initial height calculation
    if (isDesktopMode()) {
      calculateHeights();
    }

    accordions.forEach((accordion) => {
      if (!contentMap.has(accordion)) return;

      // Desktop: hover to open, closing the others
      accordion.addEventListener('mouseenter', () => {
        if (!isDesktopMode()) return;
        if (accordion.classList.contains(classes.open)) return;

        openAccordion(accordion);

        // Close other accordions
        requestAnimationFrame(() => {
          accordions.forEach((other) => {
            if (other !== accordion) {
              closeAccordion(other);
            }
          });
        });
      });

      // Touch/small screens: the card link must not navigate — tapping anywhere
      // on the card toggles it instead. Only the "Read more" button follows the link.
      accordion.addEventListener('click', (event) => {
        if (isDesktopMode()) return;

        const target = event.target as HTMLElement;
        if (target.closest('.button-style')) return;

        event.preventDefault();

        if (accordion.classList.contains(classes.open)) {
          closeAccordion(accordion);
        } else {
          openAccordion(accordion);
        }
      });
    });

    // Recalculate heights on resize
    let resizeTimeout: number;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => {
        if (isDesktopMode()) {
          calculateHeights();
        }

        // Update any currently open accordion
        accordions.forEach((accordion) => {
          if (accordion.classList.contains(classes.open)) {
            const content = contentMap.get(accordion);
            if (content) {
              content.style.height = getHeightForAccordion(accordion) + 'px';
            }
          }
        });
      }, 100);
    });
  });
};

export default capabilities;
